import { Outlet, useLocation } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { cn } from "@/lib/utils";
import { captureReferralCode } from "@/lib/referral/captureReferralCode";
import { IS_PRODUCTION } from "@/config/version";
import { Navbar } from "@/components/flowly/Navbar/Navbar";
import { EnvBanner } from "@/components/flowly/Navbar/EnvBanner";
import { WindDownBanner } from "@/components/flowly/WindDownBanner/WindDownBanner";
import { WIND_DOWN_BANNER_ENABLED } from "@/config/featureFlags";
import { ENV_BANNER } from "@/components/flowly/Navbar/constants";
import { Footer } from "@/components/flowly/Footer/Footer";
import { FloatingWhatsAppButton } from "@/components/flowly/WhatsAppCommunity/FloatingWhatsAppButton";
import { CookieConsent } from "@/components/flowly/CookieConsent/CookieConsent";
import { RouteSEO } from "@/components/flowly/SEO/RouteSEO";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "@/components/flowly/ErrorFallback/ErrorFallback";
import { SENTRY_TAGS, SENTRY_ERROR_TYPES } from "@/config/sentry";

export const Layout = () => {
  const location = useLocation();
  const isLandingPage = location.pathname === "/";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Capture a `?ref=CODE` referral param on first load.
  useEffect(() => {
    captureReferralCode();
  }, []);

  return (
    <div
      className={cn(
        "min-h-screen bg-background text-foreground font-sans overflow-x-clip",
        // Reserve the fixed dev banner's height so it never overlaps content.
        !IS_PRODUCTION && ENV_BANNER.pagePadding,
      )}
    >
      <RouteSEO />
      <EnvBanner />
      {WIND_DOWN_BANNER_ENABLED && isLandingPage && <WindDownBanner />}
      <Navbar />
      <main className="animate-fade-in">
        {/* Route-level error boundary — a crash in one flow shows the fallback
            for that route only; navigating away resets it (key on pathname).
            Sits outside <Suspense> so lazy chunk-load failures are caught too. */}
        <Sentry.ErrorBoundary
          key={location.pathname}
          fallback={<ErrorFallback />}
          beforeCapture={(scope) => {
            scope.setTag(SENTRY_TAGS.TYPE, SENTRY_ERROR_TYPES.ROUTE_ERROR);
            scope.setTag(SENTRY_TAGS.ROUTE, location.pathname);
          }}
        >
          {/* Route-level code-split boundary. The fallback intentionally has no
              <h1> — the prerenderer waits for `main h1` to know the real page
              (not this fallback) has rendered. */}
          <Suspense fallback={<div className="min-h-[60vh]" />}>
            <Outlet />
          </Suspense>
        </Sentry.ErrorBoundary>
      </main>
      <Footer />
      <FloatingWhatsAppButton />
      <CookieConsent />
    </div>
  );
};

export default Layout;
